import {
  rawExecuteSchema,
  Schema,
  SchemaType,
  ValidationContext,
} from './core';

export const string: Schema<string> = {
  validate(value: unknown): value is string {
    return typeof value === 'string';
  },
  getDescription() {
    return 'string';
  },
};

export const number: Schema<number> = {
  validate(value: unknown): value is number {
    return typeof value === 'number' && !Number.isNaN(value);
  },
  getDescription() {
    return 'number';
  },
};

export const nan: Schema<number> = {
  validate(value: unknown): value is number {
    return Number.isNaN(value);
  },
  getDescription() {
    return 'NaN';
  },
};

export const any: Schema<any> = {
  validate(value: any): value is any {
    return true;
  },
  getDescription() {
    return 'any';
  },
};

export const unknown: Schema<unknown> = {
  validate(value: unknown): value is unknown {
    return true;
  },
  getDescription() {
    return 'unknown';
  },
};

export const boolean: Schema<boolean> = {
  validate(value: unknown): value is boolean {
    return typeof value === 'boolean';
  },
  getDescription() {
    return 'boolean';
  },
};

export const bigint: Schema<bigint> = {
  validate(value: unknown): value is bigint {
    return typeof value === 'bigint';
  },
  getDescription() {
    return 'bigint';
  },
};

export function literal<
  T extends string | number | boolean | Symbol | undefined | null,
>(value: T): Schema<T> {
  let description: string;
  return {
    validate(_value: unknown): _value is T {
      return _value === value;
    },

    getDescription() {
      if (!description) {
        description = typeof value === 'string' ? `'${value}'` : `${value}`;
      }
      return description;
    },
  };
}

export function optional<T>(schema: Schema<T>): Schema<T | null | undefined> {
  let description: string;
  return {
    validate(value: unknown, validateChild): value is T | null | undefined {
      if (value === undefined || value === null) return true;
      validateChild(schema, value, undefined);
      return true;
    },
    getDescription() {
      if (!description) {
        description = `Maybe<${schema.getDescription()}>`;
      }
      return description;
    },
  };
}

export function union<T extends Schema<any>[]>(
  ...schemas: T
): Schema<SchemaType<T[number]>> {
  let description: string;
  return {
    validate(value, __, ctx): value is SchemaType<T[number]> {
      const childCtx: ValidationContext = {
        quick: ctx.quick,
        schemaViolations: [],
      };
      for (let i = 0; i < schemas.length; i++) {
        if (rawExecuteSchema(childCtx, schemas[i], value).type === 'pass') {
          return true;
        }
      }
      Array.prototype.push.apply(
        ctx.schemaViolations,
        childCtx.schemaViolations
      );
      return false;
    },
    getDescription() {
      if (description) {
        return description;
      } else if (schemas.length === 1) {
        description = schemas[0].getDescription();
      } else if (schemas.length === 0) {
        description = 'never';
      } else {
        const descriptionLines = [''];
        for (const schema of schemas) {
          const [first, ...rest] = schema.getDescription().split('\n');
          descriptionLines.push(
            `  | ${first}`,
            ...rest.map((line) => `    ${line}`)
          );
        }
        descriptionLines.push('');
        description = descriptionLines.map((line) => line.trimEnd()).join('\n');
      }
      return description;
    },
  };
}

type UnionToIntersection<T> = (T extends any ? (x: T) => any : never) extends (
  x: infer R
) => any
  ? R
  : never;

export function intersection<T extends Schema<any>[]>(
  ...schemas: T
): Schema<UnionToIntersection<SchemaType<T[number]>>> {
  let description: string;
  return {
    validate(
      value,
      __,
      ctx
    ): value is UnionToIntersection<SchemaType<T[number]>> {
      for (let i = 0; i < schemas.length; i++) {
        if (rawExecuteSchema(ctx, schemas[i], value).type === 'fail') {
          return false;
        }
      }
      return true;
    },
    getDescription() {
      if (description) {
        return description;
      } else if (schemas.length === 1) {
        description = schemas[0].getDescription();
      } else if (schemas.length === 0) {
        description = 'unknown';
      } else {
        const descriptionLines = [''];
        for (const schema of schemas) {
          const [first, ...rest] = schema.getDescription().split('\n');
          descriptionLines.push(
            `  & ${first}`,
            ...rest.map((line) => `    ${line}`)
          );
        }
        descriptionLines.push('');
        description = descriptionLines.map((line) => line.trimEnd()).join('\n');
      }
      return description;
    },
  };
}

type MappedTupleSchemaType<T extends Schema<any>[]> = {
  [K in keyof T]: SchemaType<T[K]>;
};

export function tuple<T extends Schema<any>[]>(
  ...descriptorTuple: T
): Schema<MappedTupleSchemaType<T>> {
  let description: string;
  return {
    validate(value: unknown, validateChild): value is MappedTupleSchemaType<T> {
      if (!Array.isArray(value) || value.length !== descriptorTuple.length) {
        return false;
      }
      for (let i = 0; i < descriptorTuple.length; i++) {
        validateChild(descriptorTuple[i], (value as any)[i], i);
      }
      return true;
    },
    getDescription() {
      if (description) {
        return description;
      } else if (descriptorTuple.length === 0) {
        description = '[]';
      } else {
        const descriptionLines = ['['];
        for (const schema of descriptorTuple) {
          descriptionLines.push(
            ...`${schema.getDescription()},`
              .split('\n')
              .map((line) => `  ${line}`.trimEnd())
          );
        }
        descriptionLines.push(']');
        description = descriptionLines.join('\n');
      }
      return description;
    },
  };
}

export function array<T>(schema: Schema<T>): Schema<Array<T>> {
  let description: string;
  return {
    validate(value: unknown, validateChild): value is Array<T> {
      if (!Array.isArray(value)) {
        return false;
      }

      for (let i = 0; i < value.length; i++) {
        validateChild(schema, value[i], i);
      }

      return true;
    },
    getDescription() {
      if (!description) {
        description = `Array<${schema.getDescription()}>`;
      }
      return description;
    },
  };
}

type MappedObjectSchemaType<T extends Record<string, Schema<any>>> = {
  [K in keyof T]: SchemaType<T[K]>;
};

export function object<T extends Record<string, Schema<any>>>(
  descriptorObj: T
): Schema<MappedObjectSchemaType<T>> {
  const keys = Object.keys(descriptorObj);
  let description: string;
  return {
    validate(
      value: unknown,
      validateChild
    ): value is MappedObjectSchemaType<T> {
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
      }

      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        validateChild(descriptorObj[key], (value as any)[key], key);
      }

      return true;
    },
    getDescription() {
      if (description) {
        return description;
      } else if (keys.length === 0) {
        description = '{}';
      } else {
        const descriptionLines = ['{'];
        for (const key of keys) {
          const [first, ...rest] = descriptorObj[key]
            .getDescription()
            .split('\n');
          descriptionLines.push(
            `  ${key}: ${first}`,
            ...rest.map((line) => `  ${line}`)
          );
          descriptionLines.push(`${descriptionLines.pop()!},`);
        }
        descriptionLines.push('}');
        description = descriptionLines.map((line) => line.trimEnd()).join('\n');
      }
      return description;
    },
  };
}

export function record<T, K extends Schema<string>>(
  keysSchema: K,
  valuesSchema: Schema<T>
): Schema<Record<SchemaType<K>, T>> {
  let description: string;
  return {
    validate(value: unknown, validateChild): value is Record<string, T> {
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
      }
      const keys = Object.keys(value);
      for (let i = 0; i < keys.length; i++) {
        validateChild(valuesSchema, (value as any)[keys[i]], keys[i]);
        if (keysSchema) {
          validateChild(keysSchema, keys[i], keys[i]);
        }
      }
      return true;
    },
    getDescription() {
      if (!description) {
        description = `Record<${
          keysSchema ? keysSchema.getDescription() : 'string'
        }, ${valuesSchema.getDescription()}>`;
      }
      return description;
    },
  };
}

export function custom<T>(
  description: string,
  validator: (
    value: unknown,
    validateChild: (
      schema: Schema<unknown>,
      value: unknown,
      key?: string | number
    ) => undefined,
    ctx: ValidationContext
  ) => value is T
): Schema<T> {
  return {
    validate: validator,
    getDescription() {
      return description;
    },
  };
}

type BuilderReturns<Ts extends any[]> = {
  [K in keyof Ts]: Schema<Ts[K]>;
};

type Builders<Ts extends any[]> = {
  [K in keyof Ts]: (args: BuilderReturns<Ts>) => Schema<Ts[K]>;
};

let referenceId = 0;
export function multiRecursive<Ts extends any[]>(
  ...builders: Builders<Ts>
): BuilderReturns<Ts> {
  const refs = builders.map(() => ({})) as BuilderReturns<Ts>;

  for (let i = 0; i < builders.length; i++) {
    const id = ++referenceId;
    const ref = refs[i];
    const { getDescription, validate } = builders[i](refs);
    if (!getDescription || !validate) {
      throw new Error('Detected strong recursion');
    }
    ref.validate = validate;
    let describing = false;
    ref.getDescription = () => {
      if (describing) return `${id}#__self_reference__`;
      try {
        describing = true;
        const result = `${id}#(${getDescription()})`;
        return result;
      } finally {
        describing = false;
      }
    };
  }

  return refs;
}

export function recursive<T>(builder: (self: Schema<T>) => Schema<T>) {
  const [result] = multiRecursive<[T]>(([self]) => builder(self));
  return result;
}
